/**
 * Sidebar order per portal, and the Dashboard's tab order (Admin Setup →
 * Branding, Platform Settings → Platform).
 *
 * Lists every item of the tenant's customer and supplier menus, and every
 * Dashboard tab switched on for either side, in their current order; drag or
 * the up / down buttons re-order, Reset restores the default, Save stores the
 * order instance-wide through the menu-order endpoint. Items switched off keep
 * their place, so the order survives switching them back on.
 */

import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Checkbox } from '../../ui/checkbox';
import { ArrowDown, ArrowUp, GripVertical, ListOrdered, Loader2, RotateCcw } from 'lucide-react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { platformSettingsAPI, type MenuOrder } from '../../../lib/api';
import { applyMenuOrder, describeDefaultOrder, type NavKey, type NavOrderRow } from '../../../config/navigation';
import { DASHBOARD_TAB_REGISTRY, orderDashboardTabs } from '../../dashboard/types';
import { useMenuSettings } from '../../../contexts/MenuSettingsContext';
import { toast } from 'sonner';

type Portal = 'customer' | 'supplier';
type Column = Portal | 'dashboard';
const TAB_NAMES: Record<string, string> = Object.fromEntries(DASHBOARD_TAB_REGISTRY.map((t) => [t.id, t.label]));
const DEFAULT_TAB_ORDER = DASHBOARD_TAB_REGISTRY.map((t) => t.id);

function move<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

interface SortableRowProps {
  id: string;
  index: number;
  name: string;
  isFirst: boolean;
  isLast: boolean;
  disabled: boolean;
  onUp: () => void;
  onDown: () => void;
  testId: string;
  /** Present on the customer menu: show this item to admins only. */
  adminOnly?: boolean;
  onToggleAdminOnly?: () => void;
}

/** One menu item: drag by the grip (or anywhere on the row), or use the arrows. */
function SortableRow({ id, index, name, isFirst, isLast, disabled, onUp, onDown, testId, adminOnly, onToggleAdminOnly }: SortableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  const style = {
    transform: transform ? `translate3d(${Math.round(transform.x)}px, ${Math.round(transform.y)}px, 0)` : undefined,
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <li ref={setNodeRef} style={style} data-testid={testId}
        className="flex items-center justify-between px-2 py-2 text-sm bg-background">
      <span className="flex items-center gap-2">
        <button type="button" aria-label={`Drag ${name}`} className="cursor-grab active:cursor-grabbing text-muted-foreground touch-none"
                {...attributes} {...listeners} disabled={disabled}>
          <GripVertical className="h-4 w-4" />
        </button>
        <span className="w-5 text-xs text-muted-foreground tabular-nums">{index + 1}</span>
        {name}
      </span>
      <span className="flex items-center gap-1">
        {onToggleAdminOnly && (
          <label className="mr-2 flex items-center gap-1.5 text-xs text-muted-foreground" title="Only users who manage the company see this item">
            <Checkbox checked={!!adminOnly} onCheckedChange={onToggleAdminOnly} disabled={disabled}
                      aria-label={`${name}: admins only`} data-testid={`${testId}-admin-only`} />
            Admins only
          </label>
        )}
        <Button variant="ghost" size="icon" className="h-7 w-7" aria-label={`Move ${name} up`} onClick={onUp} disabled={isFirst || disabled}>
          <ArrowUp className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" className="h-7 w-7" aria-label={`Move ${name} down`} onClick={onDown} disabled={isLast || disabled}>
          <ArrowDown className="h-4 w-4" />
        </Button>
      </span>
    </li>
  );
}

export function MenuOrderSetting() {
  const { isSpendAnalyticsMode, isComplianceMode, menuVisibility, isFeatureEnabled, isDashboardTabEnabled } = useMenuSettings();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [order, setOrder] = useState<Record<Portal, NavKey[]>>({ customer: [], supplier: [] });
  const [dirty, setDirty] = useState(false);
  const [adminOnly, setAdminOnly] = useState<Set<string>>(new Set());
  const [tabOrder, setTabOrder] = useState<string[]>(DEFAULT_TAB_ORDER);

  const defaults = useMemo(() => ({
    customer: describeDefaultOrder({ portalMode: 'customer', isSpendAnalytics: isSpendAnalyticsMode, isCompliance: isComplianceMode }),
    supplier: describeDefaultOrder({ portalMode: 'supplier', isSpendAnalytics: isSpendAnalyticsMode, isCompliance: isComplianceMode }),
  }), [isSpendAnalyticsMode, isComplianceMode]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await platformSettingsAPI.getMenuOrder();
        setOrder({
          customer: applyMenuOrder(defaults.customer.map((d) => d.key), data.customer),
          supplier: applyMenuOrder(defaults.supplier.map((d) => d.key), data.supplier),
        });
        setAdminOnly(new Set(data.admin_only ?? []));
        setTabOrder(orderDashboardTabs(DASHBOARD_TAB_REGISTRY, data.dashboard_tabs).map((t) => t.id));
      } catch (err: unknown) {
        console.error('Error loading menu order:', err);
        toast.error('Failed to load the menu order');
        setOrder({ customer: defaults.customer.map((d) => d.key), supplier: defaults.supplier.map((d) => d.key) });
      } finally {
        setLoading(false);
      }
    })();
  }, [defaults]);

  const rowOf = (portal: Portal, key: NavKey): NavOrderRow | undefined => defaults[portal].find((d) => d.key === key);
  const nameOf = (portal: Portal, key: NavKey) => rowOf(portal, key)?.name ?? key;

  /** Off in this tenant: a menu-visibility key switched off, or a feature flag off. */
  const isHidden = (portal: Portal, key: NavKey): boolean => {
    const row = rowOf(portal, key);
    if (!row) return false;
    if (row.featureFlag && !isFeatureEnabled(row.featureFlag as Parameters<typeof isFeatureEnabled>[0])) return true;
    const visKey = portal === 'supplier' && row.supplierVisibilityKey ? row.supplierVisibilityKey : row.visibilityKey;
    return !!visKey && menuVisibility[visKey] === false;
  };

  const split = (portal: Portal, keys: NavKey[]) => ({
    visible: keys.filter((k) => !isHidden(portal, k)),
    hidden: keys.filter((k) => isHidden(portal, k)),
  });

  /** Dashboard tabs switched on for customers or suppliers are ordered; the rest keep their place after them. */
  const splitTabs = (ids: string[]) => {
    const on = (id: string) => isDashboardTabEnabled(id, false) || isDashboardTabEnabled(id, true);
    return { visible: ids.filter(on), hidden: ids.filter((id) => !on(id)) };
  };

  const shift = (portal: Column, index: number, delta: number) => {
    if (portal === 'dashboard') {
      setTabOrder((prev) => {
        const { visible, hidden } = splitTabs(prev);
        return [...move(visible, index, index + delta), ...hidden];
      });
    } else {
      setOrder((prev) => {
        const { visible, hidden } = split(portal, prev[portal]);
        return { ...prev, [portal]: [...move(visible, index, index + delta), ...hidden] };
      });
    }
    setDirty(true);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = (portal: Column) => (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    if (portal === 'dashboard') {
      setTabOrder((prev) => {
        const { visible, hidden } = splitTabs(prev);
        const from = visible.indexOf(String(active.id));
        const to = visible.indexOf(String(over.id));
        return from < 0 || to < 0 ? prev : [...arrayMove(visible, from, to), ...hidden];
      });
      setDirty(true);
      return;
    }
    setOrder((prev) => {
      const { visible, hidden } = split(portal, prev[portal]);
      const from = visible.indexOf(active.id as NavKey);
      const to = visible.indexOf(over.id as NavKey);
      if (from < 0 || to < 0) return prev;
      return { ...prev, [portal]: [...arrayMove(visible, from, to), ...hidden] };
    });
    setDirty(true);
  };

  const toggleAdminOnly = (key: string) => {
    setAdminOnly((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
    setDirty(true);
  };

  const reset = (portal: Column) => {
    if (portal === 'dashboard') setTabOrder(DEFAULT_TAB_ORDER);
    else setOrder((prev) => ({ ...prev, [portal]: defaults[portal].map((d) => d.key) }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload: MenuOrder = {
        customer: order.customer, supplier: order.supplier, admin_only: Array.from(adminOnly),
        dashboard_tabs: splitTabs(tabOrder).visible,
      };
      await platformSettingsAPI.patchMenuOrder(payload);
      setDirty(false);
      toast.success('Menu order saved. Reloading to apply it.');
      window.setTimeout(() => window.location.reload(), 800);
    } catch (err: unknown) {
      console.error('Error saving menu order:', err);
      toast.error('Failed to save the menu order');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const column = (portal: Column, title: string) => {
    const { visible, hidden } = portal === 'dashboard' ? splitTabs(tabOrder) : split(portal, order[portal]);
    const nameFor = (key: string) => (portal === 'dashboard' ? TAB_NAMES[key] ?? key : nameOf(portal, key as NavKey));
    return (
      <div className="space-y-2" data-testid={`menu-order-${portal}`}>
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold">{title}</h4>
          <Button variant="ghost" size="sm" onClick={() => reset(portal)} disabled={saving}>
            <RotateCcw className="h-4 w-4 mr-1" /> Reset
          </Button>
        </div>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd(portal)}>
          <SortableContext items={visible} strategy={verticalListSortingStrategy}>
            <ol className="rounded-md border border-input divide-y divide-input bg-background">
              {visible.map((key, index) => (
                <SortableRow key={key} id={key} index={index} name={nameFor(key)}
                             isFirst={index === 0} isLast={index === visible.length - 1} disabled={saving}
                             onUp={() => shift(portal, index, -1)} onDown={() => shift(portal, index, 1)}
                             testId={`menu-order-${portal}-${key}`}
                             adminOnly={adminOnly.has(key)}
                             onToggleAdminOnly={portal === 'customer' ? () => toggleAdminOnly(key) : undefined} />
              ))}
            </ol>
          </SortableContext>
        </DndContext>
        {portal === 'dashboard' ? (
          <p className="text-xs text-muted-foreground">
            The Dashboard opens on the first tab. Tabs switched off for both customers and suppliers are not listed.
          </p>
        ) : hidden.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Switched off elsewhere, so not shown: {hidden.map((k) => nameFor(k)).join(', ')}.
          </p>
        )}
      </div>
    );
  };

  return (
    <Card data-testid="menu-order-setting">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ListOrdered className="h-5 w-5" />
              Menu order
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Put the side menu and the Dashboard tabs in the order your users work, and mark items only admins should see. Use the arrows, then save.
            </p>
          </div>
          <Button onClick={save} disabled={!dirty || saving} data-testid="menu-order-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Save order
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {column('customer', 'Customer menu')}
          {column('supplier', 'Supplier menu')}
          {column('dashboard', 'Dashboard tabs')}
        </div>
      </CardContent>
    </Card>
  );
}
